// File: src/providers/PaymentProvider.tsx
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import apiRequest, { ApiResponse } from "../axios/useApi";
import { formatDate, handleApiError } from "../lib/utils";
import { UIContext } from "./UIProvider";

export interface ReceivablePayment {
  receivablePaymentId: string; // GUID can be represented as a string in TypeScript
  businessMasterId: string; // GUID as a string
  customerId: string; // GUID as a string
  paymentDate: Date; // DateTime is represented as Date in TypeScript
  timeZoneId: number; // short type in C# can be a number in TypeScript
  methodId: number; // int is a number in TypeScript
  accountId: string; // GUID as a string
  externalId: string; // string type
  redemptionTypeId: number; // int type
  redemptionTypeAccountId: string; // GUID as a string
  referenceNumber: string; // string type
  totalAmt: number; // decimal type in C# can be a number in TypeScript
  isCancelled: boolean; // boolean type
  cancelledDateTime: Date; // DateTime type
  customerName: string;
  createdDateTime: Date; // DateTime type
  updatedDateTime: Date; // DateTime type
}

interface PaymentFilters {
  startDate?: string; // ISO string for the start date
  endDate?: string; // ISO string for the end date
  customerId?: string; // Customer ID as a string
  status?: string; // Optional status field if needed
}

interface PaymentMethod {
  paymentMethodId: string;
  businessMasterId: string;
  paymentMethodName: string;
  isActive: boolean;
}

export const PaymentContext = createContext({
  paymentData: [] as ReceivablePayment[],
  paymentMethodOptions: [] as { label: string; value: string }[],
  isLoading: false,
  pageSize: 10,
  pageNumber: 1,
  totalPages: 0,
  totalRecords: 0,
  handlePageChange: (page: number) => {},
  handlePageSizeChange: (size: number) => {},
  handleApplyFilter: (filterValues: any) => {},
  handleClearFilter: () => {},
  getPaymentData: (
    pageNumber: number,
    pageSize: number,
    filterValues: any
  ) => {}, // Add
});

type Props = PropsWithChildren;

export const PaymentProvider = ({ children }: Props) => {
  const { onError, onSuccess, businessId } = useContext(UIContext);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentData, setPaymentData] = useState<ReceivablePayment[]>([]);
  const [paymentMethodOptions, setpaymentMethodOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [filterValues, setFilterValues] = useState<PaymentFilters>({});
  // Pagination state
  const [pageSize, setPageSize] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const handleApplyFilter = async (filterValues: PaymentFilters) => {
    setFilterValues(filterValues);
    await getPaymentData(1, pageSize, filterValues);
  };

  const handleClearFilter = () => {
    setFilterValues({});
    getPaymentData(1, pageSize, {});
  };

  const getPaymentData = async (
    page: number,
    size: number,
    filterValues?: any
  ) => {
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams({
        pageSize: size.toString(),
      });

      if (filterValues?.startDate) {
        queryParams.append("startDate", formatDate(filterValues.startDate));
      }
      if (filterValues?.endDate) {
        queryParams.append("endDate", formatDate(filterValues.endDate));
      }
      if (filterValues?.status) {
        queryParams.append("paymentMethod", filterValues.status);
      }
      if (filterValues?.customerId) {
        queryParams.append("customerId", filterValues.customerId);
      }

      const response: ApiResponse<{
        totalPages: number;
        totalRecords: number;
        pageNumber: number;
        pageSize: number;
        items: ReceivablePayment[];
        message: string;
        errorDetail: any;
      }> = await apiRequest({
        url: `/Payment/${businessId}/List/${page}?${queryParams}`,
        method: "GET",
      });

      if (response.success) {
        onSuccess?.(
          "GET",
          "getPayments",
          response?.message || "Data retrieved successfully!"
        );
        const { totalPages, items, pageNumber } = response?.data || {};
        setTotalRecords(response?.data?.totalRecords || 0);
        setTotalPages(totalPages || 0);
        setPageNumber(pageNumber || 1);
        setPaymentData(items || []);
      } else {
        handleApiError(response, onError);
      }
    } catch (error) {
      onError?.(new Error("An error occurred while fetching payment data."));
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch customers on page or pageSize change
  useEffect(() => {
    getPaymentData(pageNumber, pageSize, filterValues);
  }, [pageNumber, pageSize]);

  // Handle page size change
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPageNumber(1); // Reset to the first page
  };

  // Handle page number change
  const handlePageChange = (page: number) => {
    setPageNumber(page);
  };
  const fetchPaymentMethod = async () => {
    // if (!allowableFields?.includes("status")) return;
    try {
      const response: ApiResponse<any> = await apiRequest({
        url: `/Payment/${businessId}/Paymentmethod`,
        method: "GET",
      });
      if (response.success && response.data) {
        onSuccess?.(
          "OPTIONS",
          "fetchPaymentMethod",
          response?.message || "Data retrieved successfully!"
        );
        const options = response?.data?.items.map(
          (paymentMethod: PaymentMethod) => ({
            label: paymentMethod.paymentMethodName,
            value: paymentMethod.paymentMethodId,
          })
        );
        setpaymentMethodOptions(options || []);
      } else {
        handleApiError(response, onError);
      }
    } catch (error) {
      onError?.(new Error("An error occurred while fetching payment methods."));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentMethod();
  }, []);

  return (
    <PaymentContext.Provider
      value={{
        paymentData,
        isLoading,
        pageSize,
        pageNumber,
        totalPages,
        totalRecords,
        handlePageChange,
        handlePageSizeChange,
        handleApplyFilter,
        handleClearFilter,
        paymentMethodOptions, // Add this line to provide the options
        getPaymentData, // Add this line to provide the function
      }}
    >
      {children}
    </PaymentContext.Provider>
  );
};
